import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { seo } from '~/util';
import Load from '~/components/Load';
import { useEffect, useRef } from 'react';
import { useAuth } from '~/components/Providers/AuthProvider';
import { completeLoginServer, startLoginServer } from '~/auth/login';

export const Route = createFileRoute('/login')({
    validateSearch: z.object({
        token: z.string().min(1).optional(),
    }),
    loaderDeps: ({ search: { token } }) => ({ token }),
    loader: async ({ deps }) => {
        if (!deps.token) {
            await startLoginServer();
        }
    },
    head: () => ({
        meta: seo({
            title: 'Login',
            description: 'Login to the AREDL through discord',
        }),
    }),
    component: LoginCallback,
});

function LoginCallback() {
    const token = Route.useSearch({ select: ({ token }) => token });
    const navigate = Route.useNavigate();
    const refreshAuthData = useAuth()?.refreshAuthData;
    const completedToken = useRef<string | null>(null);

    useEffect(() => {
        if (!token) return;
        if (completedToken.current === token) return;
        completedToken.current = token;

        completeLoginServer({ data: { token } })
            .then(async ({ ok, showToast }) => {
                if (!ok) {
                    console.error('Failed to complete login:', showToast);
                } else {
                    await refreshAuthData?.();
                }

                navigate({
                    to: '/',
                    search: { showToast: showToast },
                    replace: true,
                });
            })
            .catch((error) => {
                console.error('Failed to complete login:', error);
                navigate({
                    to: '/',
                    search: { showToast: 'loginFailedUnknown' },
                    replace: true,
                });
            });
    }, [token, navigate, refreshAuthData]);

    return (
        <div className="flex flex-col h-[80vh] w-full items-center justify-center gap-4 text-center">
            <Load />
        </div>
    );
}
